import React, { useEffect, useState } from 'react';
import Modal from './ModalTrue';
import { HoldReviewSection } from './HoldReviewSection';
import { fetchFeeDecisionOptions, WaiverReasonOption } from '../api/cancellationRingfence';
import { heldCancellationAlert, CancelClaimResponse } from '../api/claims';

interface HoldReviewModalProps {
  taskId: string;
  requestedReason: string | null;
  clientEmail: string | null;
  onClose: () => void;
  onCompleted: () => void;
}

/**
 * Amendment 3 (19 Aug 2026, Ryan): the inline "on the phone" path. Opened
 * directly from a staff cancel flow the moment cancel_claims returns held,
 * bound to the just-created hold task -- so the review never needs a trip
 * through the Manual lane. Reuses the exact script, endpoints and outcomes
 * as CancellationRingfencePanel's hold branch via the shared
 * HoldReviewSection. `canAct` is always true here: a task this fresh has
 * not been completed or archived by anyone.
 */
export const HoldReviewModal: React.FC<HoldReviewModalProps> = ({
  taskId, requestedReason, clientEmail, onClose, onCompleted,
}) => {
  const [reasons, setReasons] = useState<WaiverReasonOption[]>([]);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [reviewCompleted, setReviewCompleted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Nothing has been decided yet, so the chargeable count is unknowable --
    // 0 is the honest input. HoldReviewSection never reads `fee`, only
    // `reasons`, so this is a strict subset of what the panel fetches.
    fetchFeeDecisionOptions(0)
      .then((data) => {
        if (cancelled) return;
        if (!data || !Array.isArray(data.reasons) || data.reasons.length === 0) {
          setOptionsError('Could not load the fee decision options.');
          return;
        }
        setReasons(data.reasons);
      })
      .catch(() => { if (!cancelled) setOptionsError('Could not load the fee decision options.'); });
    return () => { cancelled = true; };
  }, [taskId]);

  const handleReviewed = () => {
    setReviewCompleted(true);
    onClose();
    onCompleted();
    // eslint-disable-next-line no-alert
    alert('The over-70 review has been recorded. No further action is needed on this task.');
  };

  // Finding 2 (fix wave, 19 Aug 2026): closing the modal WITHOUT completing
  // the review (client hung up, questions failed to load, staff changed
  // their mind) must not leave staff thinking nothing more is needed -- the
  // claim(s) are still held and a task is still waiting. Reuses
  // heldCancellationAlert's fallback copy so the wording can never drift
  // from the table-level alert shown on the "No" branch of the same flow.
  const handleDismiss = () => {
    if (!reviewCompleted) {
      // eslint-disable-next-line no-alert
      alert(heldCancellationAlert({} as CancelClaimResponse));
    }
    onClose();
  };

  return (
    <Modal onClose={handleDismiss}>
      <div style={{ padding: '8px 4px', minWidth: '320px' }}>
        <h3 style={{ margin: '0 0 8px 0' }}>Over-70 review</h3>
        {optionsError && <div className="ringfence__error" role="alert">{optionsError}</div>}
        <HoldReviewSection
          taskId={taskId}
          canAct={true}
          reasons={reasons}
          requestedReason={requestedReason}
          clientEmail={clientEmail}
          onChanged={handleReviewed}
        />
      </div>
    </Modal>
  );
};

export default HoldReviewModal;
